/**
 * 参加の申し込み（決定34）。
 *
 * 名簿に載っていない方が、アプリで電話番号＋認証番号を入れて「その番号の持ち主」だと確かめたあと、
 * お名前と拠点を入れて申し込んだものの一覧です。ここで承認すると名簿に入り、
 * 全社と所属拠点の公式・お知らせのトークに入って、ご本人のアプリがそのまま使えるようになります（決定31）。
 *
 * 決めごと（決定22・決定34）:
 *  - 承認は**ボタン1つ**（確認の窓をはさまない）。押したら、何が起きたかを言葉で出す
 *  - ただし**名簿に同じお名前の方がいる**ときは、確認の窓で「名簿のその方として承認する」か
 *    「新しい方として承認する」かを選んでいただく（電話番号を変えた方・名簿で電話番号が空の方が、
 *    同じ方として2人登録されるのを防ぐ・#337 #341）。名簿の方として承認すると、名簿の電話番号を
 *    申し込みの番号に差し替え、前の番号ではログインできなくなる
 *  - 断るときだけ確認の窓をはさむ。理由は任意で、ご本人の画面に表示される
 *  - 電話番号は出す（承認の前に、管理者からご本人へ確認の電話をかけられるように）
 *  - 拠点管理者には自拠点への申し込みだけが出る（サーバが絞る・画面で隠しているのではない）
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import { loadFailure, nextStepFor, type LoadFailure } from '../api/load-failure.js';
import type {
  JoinRequestApproved,
  JoinRequestCandidate,
  JoinRequestItem,
  JoinRequestList,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { formatTableDateTime } from '../format/datetime.js';
import { formatPhone } from '../format/phone.js';
import { notifyMenuCountsChanged } from '../menu/badge-refresh.js';
import { endsWithRequest, withReason } from '../format/sentence.js';

/** 断る理由の長さの上限（サーバと同じ）。 */
const REJECT_REASON_MAX_LENGTH = 200;

/** 結果のお知らせ1つ分。 */
interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
  /** 承認したときに開ける、その方のメンバーの画面 */
  memberId?: string;
}

/** 「新しい方として承認する」を選んだとき（名簿の方の ID の代わり）。 */
const AS_NEW = 'new';

/** 名簿の電話番号の出し方（下4桁だけ・未登録ならその旨）。 */
function candidatePhone(candidate: JoinRequestCandidate): string {
  return candidate.phone_hint === null
    ? '電話番号は未登録'
    : `電話番号の下4桁 ${candidate.phone_hint.slice(-4)}`;
}

/** 名簿の方の見分けに使う一行（拠点・役職・スタッフID・電話番号・状態）。 */
function candidateDetail(candidate: JoinRequestCandidate): string {
  return [
    candidate.site_name ?? '拠点の指定なし',
    candidate.role_display_name,
    candidate.staff_code ? `スタッフID ${candidate.staff_code}` : 'スタッフIDなし',
    candidatePhone(candidate),
    candidate.status === 'invited' ? 'まだログインしていない方' : '利用中の方',
  ].join('・');
}

/** お名前の出し方（申し込みの途中などで空のときも画面を壊さない）。 */
function nameOf(item: Pick<JoinRequestItem, 'display_name'>): string {
  const name = (item.display_name ?? '').trim();
  return name === '' ? 'お名前の無い方' : name;
}

export function JoinRequestsPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const isOrg = profile?.grant.scope === 'org';
  const [pending, setPending] = useState<JoinRequestItem[] | null>(null);
  const [decided, setDecided] = useState<JoinRequestItem[] | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [decidedError, setDecidedError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<Banner | null>(null);
  /** いま処理している申し込み（ボタンの二度押しを防ぐ） */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** 断る確認の窓を出している申し込み */
  const [rejecting, setRejecting] = useState<JoinRequestItem | null>(null);
  const [reason, setReason] = useState('');
  /** 名簿に同じお名前の方がいて、どちらとして承認するかを選んでいただいている申し込み */
  const [choosing, setChoosing] = useState<JoinRequestItem | null>(null);
  /** 選んだもの（名簿の方の ID か、AS_NEW）。はじめは何も選ばない */
  const [choice, setChoice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setDecidedError(null);
    api
      .get<JoinRequestList>('/admin/join-requests', { query: { view: 'pending' } })
      .then((loaded) => {
        if (!cancelled) setPending(loaded.items);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setPending(null);
          setError(loadFailure(caught));
        }
      });
    api
      .get<JoinRequestList>('/admin/join-requests', { query: { view: 'decided' } })
      .then((loaded) => {
        if (!cancelled) setDecided(loaded.items);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setDecided(null);
          setDecidedError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const reload = (): void => {
    setReloadKey((n) => n + 1);
    // 承認・お断りのあとは、左の赤丸も数え直す
    notifyMenuCountsChanged();
  };

  /** 「承認する」を押したとき。名簿に同じお名前の方がいれば、先に選んでいただく。 */
  function startApprove(item: JoinRequestItem): void {
    if ((item.candidates ?? []).length > 0) {
      setChoice(null);
      setChoosing(item);
      return;
    }
    void approve(item, null);
  }

  async function approve(item: JoinRequestItem, asUserId: string | null): Promise<void> {
    setBusyId(item.id);
    setResult(null);
    try {
      const approved = await api.post<JoinRequestApproved>(
        `/admin/join-requests/${item.id}/approve`,
        asUserId !== null ? { body: { as_user_id: asUserId } } : undefined,
      );
      if (approved.as_existing) {
        const previous = approved.previous_phone_hint ?? null;
        setResult({
          tone: 'success',
          message: `メンバーの${approved.display_name}さんとして承認しました。メンバーの電話番号を、申し込みの番号（${formatPhone(item.phone)}）に差し替えました。`,
          nextStep:
            (previous === null
              ? ''
              : `前の電話番号（下4桁 ${previous.slice(-4)}）では、もうログインできません。` +
                (approved.signed_out_elsewhere === true
                  ? '前のスマホでログインしていた分も切れました。'
                  : '')) +
            '拠点・スタッフID・役職・参加中のトークは、そのまま引き継いでいます。ご本人のアプリがそのまま使えるようになります。',
          memberId: approved.user_id,
        });
      } else {
        setResult({
          tone: 'success',
          message: `${approved.display_name}さんをメンバーとして登録しました。${joinedChannelsSentence(approved)}`,
          nextStep:
            'ご本人のアプリがそのまま使えるようになります。役職を変えるときは、メンバーの画面から行ってください。',
          memberId: approved.user_id,
        });
      }
      reload();
    } catch (caught) {
      setResult(failureBanner(caught, `${nameOf(item)}さんの申し込みを承認できませんでした。`));
      reload();
    } finally {
      setBusyId(null);
      setChoosing(null);
      setChoice(null);
    }
  }

  async function reject(): Promise<void> {
    if (!rejecting) return;
    const item = rejecting;
    const trimmed = reason.trim();
    setBusyId(item.id);
    setResult(null);
    try {
      await api.post(`/admin/join-requests/${item.id}/reject`, {
        body: trimmed === '' ? {} : { reason: trimmed },
      });
      setResult({
        tone: 'success',
        message: `${nameOf(item)}さんの申し込みを断りました。`,
        nextStep:
          trimmed === ''
            ? 'ご本人のアプリに、お断りしたことが表示されます。'
            : 'ご本人のアプリに、お断りしたことと理由が表示されます。',
      });
      reload();
    } catch (caught) {
      setResult(failureBanner(caught, `${nameOf(item)}さんの申し込みを断れませんでした。`));
      reload();
    } finally {
      setBusyId(null);
      setRejecting(null);
      setReason('');
    }
  }

  return (
    <article>
      <PageHeader
        title="参加の申し込み"
        description={
          isOrg
            ? 'メンバーとして登録されていない方から届いた、参加の申し込みです。ご本人か確かめてから承認してください。'
            : '自拠点への参加の申し込みです。ご本人か確かめてから承認してください。'
        }
      />
      <p className="admin-note">
        {
          '申し込んだ方は、アプリで電話番号に届いた認証番号を入れて、その電話番号の持ち主だと確かめてあります。承認すると「一般」の役職でメンバーとして登録され、全社と所属拠点の公式・お知らせのトークに入ります。メンバーに同じお名前の方がいるときは、電話番号を変えた方などとして「メンバーのその方として承認する」も選べます。'
        }
      </p>

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
          {...(result.memberId !== undefined
            ? {
                action: (
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to={`/members/${result.memberId}`}
                  >
                    メンバーの画面を開く
                  </Link>
                ),
              }
            : {})}
        />
      ) : null}

      <Card title="承認を待っている申し込み" description="申し込んだ順に並んでいます。">
        {error ? <ErrorBlock failure={error} onRetry={reload} /> : null}
        {!error && !pending ? <LoadingBlock label="申し込みを読み込んでいます…" /> : null}
        {pending && pending.length === 0 ? (
          <EmptyBlock label="いま承認を待っている申し込みはありません。" />
        ) : null}
        {pending && pending.length > 0 ? (
          <DataTable label="承認を待っている申し込みの一覧">
            <thead>
              <tr>
                <th scope="col">お名前</th>
                <th scope="col">電話番号</th>
                {isOrg ? <th scope="col">拠点</th> : null}
                <th scope="col">申し込んだ日時</th>
                <th scope="col">
                  <span className="admin-visually-hidden">操作</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pending.map((item) => (
                <tr key={item.id}>
                  <th scope="row">
                    {nameOf(item)}
                    {(item.candidates ?? []).length > 0 ? (
                      <small className="admin-join-samename">
                        メンバーに同じお名前の方がいます（電話番号を変えた方・電話番号が未登録の方かもしれません）
                      </small>
                    ) : null}
                  </th>
                  <td>{formatPhone(item.phone)}</td>
                  {isOrg ? <td className="ui-cell-soft">{item.site_name ?? '—'}</td> : null}
                  <td className="ui-cell-soft">{formatTableDateTime(item.applied_at)}</td>
                  <td>
                    <div className="admin-actions">
                      <Button
                        variant="primary"
                        compact
                        busy={busyId === item.id && rejecting === null && choosing === null}
                        busyLabel="承認しています…"
                        disabled={busyId !== null}
                        aria-label={`${nameOf(item)}さんを承認する`}
                        onClick={() => startApprove(item)}
                      >
                        承認する
                      </Button>
                      <Button
                        variant="secondary"
                        compact
                        disabled={busyId !== null}
                        aria-label={`${nameOf(item)}さんの申し込みを断る`}
                        onClick={() => {
                          setReason('');
                          setRejecting(item);
                        }}
                      >
                        断る
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Card>

      <Card
        title="最近の対応"
        description="最近承認した・断った申し込みです（新しい順・50件まで）。"
      >
        {decidedError ? <ErrorBlock failure={decidedError} onRetry={reload} /> : null}
        {!decidedError && !decided ? <LoadingBlock /> : null}
        {decided && decided.length === 0 ? (
          <EmptyBlock label="まだ承認・お断りした申し込みはありません。" />
        ) : null}
        {decided && decided.length > 0 ? (
          <DataTable label="最近の対応の一覧">
            <thead>
              <tr>
                <th scope="col">お名前</th>
                <th scope="col">電話番号</th>
                {isOrg ? <th scope="col">拠点</th> : null}
                <th scope="col">対応</th>
                <th scope="col">対応した方</th>
                <th scope="col">日時</th>
              </tr>
            </thead>
            <tbody>
              {decided.map((item) => (
                <tr key={item.id}>
                  <th scope="row">
                    {item.status === 'approved' && item.user_id ? (
                      <Link to={`/members/${item.user_id}`}>{nameOf(item)}</Link>
                    ) : (
                      nameOf(item)
                    )}
                  </th>
                  <td>{formatPhone(item.phone)}</td>
                  {isOrg ? <td className="ui-cell-soft">{item.site_name ?? '—'}</td> : null}
                  <td className="ui-cell-wrap">{decisionLabel(item)}</td>
                  <td className="ui-cell-soft">
                    {item.decided_by_name ? `${item.decided_by_name}さん` : '—'}
                  </td>
                  <td className="ui-cell-soft">{formatTableDateTime(item.decided_at)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Card>

      <ConfirmDialog
        open={choosing !== null}
        title="承認のしかたを選んでください"
        message={
          choosing ? (
            <>
              {`${nameOf(choosing)}さん（${formatPhone(choosing.phone)}${
                choosing.site_name ? `・${choosing.site_name}` : ''
              }）の申し込みです。メンバーに同じお名前の方がいます。電話番号を変えた方や、電話番号が未登録のメンバーかもしれません。`}
              <span className="admin-choice-group" role="radiogroup" aria-label="承認のしかた">
                {(choosing.candidates ?? []).map((candidate) => (
                  <label key={candidate.user_id} className="admin-choice">
                    <input
                      type="radio"
                      name="join-approve-choice"
                      value={candidate.user_id}
                      checked={choice === candidate.user_id}
                      onChange={() => setChoice(candidate.user_id)}
                    />
                    <span className="admin-choice-body">
                      <strong>{`メンバーの${candidate.display_name}さんとして承認する`}</strong>
                      <span className="admin-choice-detail">{candidateDetail(candidate)}</span>
                      <span className="admin-choice-detail">
                        {candidate.phone_hint === null
                          ? 'このメンバーに、申し込みの電話番号を登録します。拠点・スタッフID・役職・参加中のトークは、そのまま引き継ぎます。'
                          : 'メンバーの電話番号を、申し込みの番号に差し替えます。前の電話番号ではログインできなくなり、前のスマホのログインも切れます。拠点・スタッフID・役職・参加中のトークは、そのまま引き継ぎます。'}
                      </span>
                    </span>
                  </label>
                ))}
                <label className="admin-choice">
                  <input
                    type="radio"
                    name="join-approve-choice"
                    value={AS_NEW}
                    checked={choice === AS_NEW}
                    onChange={() => setChoice(AS_NEW)}
                  />
                  <span className="admin-choice-body">
                    <strong>新しい方として承認する</strong>
                    <span className="admin-choice-detail">
                      同じお名前の別の方のときは、こちらを選んでください。メンバーをもう1人登録し、「一般」の役職で全社と所属拠点の公式・お知らせのトークに入ります。
                    </span>
                  </span>
                </label>
              </span>
            </>
          ) : (
            ''
          )
        }
        reversibility={
          choice === null
            ? '承認のしかたを選ぶと、「承認する」を押せます。'
            : choice === AS_NEW
              ? '承認したあとで、メンバーの画面からこの方を無効にできます。'
              : '差し替えた電話番号は、この画面からは元に戻せません。'
        }
        // 選ぶ前の案内は注意ではないので、ふつうの色で出す
        reversible={choice === null || choice === AS_NEW}
        tone="primary"
        confirmLabel="承認する"
        confirmDisabled={choice === null}
        busy={choosing !== null && busyId === choosing.id}
        onConfirm={() => {
          if (!choosing || choice === null) return;
          void approve(choosing, choice === AS_NEW ? null : choice);
        }}
        onCancel={() => {
          setChoosing(null);
          setChoice(null);
        }}
      />

      <ConfirmDialog
        open={rejecting !== null}
        title="参加の申し込みを断ります"
        message={
          <>
            {rejecting
              ? `${nameOf(rejecting)}さん（${formatPhone(rejecting.phone)}）の申し込みを断ります。`
              : ''}
            <label className="ui-field admin-dialog-field" htmlFor="join-reject-reason">
              <span>断る理由（なくてもかまいません）</span>
              <textarea
                id="join-reject-reason"
                rows={3}
                maxLength={REJECT_REASON_MAX_LENGTH}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <small>
                ご本人のアプリの画面に表示されます（{REJECT_REASON_MAX_LENGTH}文字まで・いま
                {reason.length}文字）。
              </small>
            </label>
          </>
        }
        reversibility="断ったあとでも、ご本人が申し込み直せば、改めて承認できます。"
        reversible
        confirmLabel="断る"
        busy={rejecting !== null && busyId === rejecting.id}
        onConfirm={() => void reject()}
        onCancel={() => {
          setRejecting(null);
          setReason('');
        }}
      />
    </article>
  );
}

/** 最近の対応の言葉。 */
function decisionLabel(item: JoinRequestItem): string {
  if (item.status === 'approved') return '承認しました';
  if (item.status === 'rejected') {
    const why = (item.reject_reason ?? '').trim();
    return why === '' ? '断りました' : `断りました（理由：${why}）`;
  }
  return '取り下げられました';
}

/** うまくいかなかったときの帯（理由と、次にすることを必ず添える）。 */
/**
 * 承認したときに入ったトークの言い方（#367 U-6）。
 * サーバが返した**実際に入ったトーク**の名前で言う。無いトーク（拠点のお知らせなど）の名前を出さない。
 */
function joinedChannelsSentence(approved: JoinRequestApproved): string {
  const names = approved.company_channel_names;
  if (names === undefined)
    return '全社・所属拠点の公式／お知らせのトークがあれば、そこに入りました。';
  if (names.length === 0) return '';
  return `${names.map((name) => `「${name}」`).join('')}のトークに入りました。`;
}

function failureBanner(caught: unknown, headline: string): Banner {
  const failure = loadFailure(caught);
  return {
    tone: 'error',
    message: withReason(headline, failure.message),
    // 理由の文が次の一手まで言っているとき（「一覧を開き直してください。」など）は足さない（#367 U-5）
    ...(endsWithRequest(failure.message)
      ? {}
      : { nextStep: nextStepFor(failure.status, failure.retryable).nextStep }),
  };
}
